// Performance (plan step 3.4; D14, D15, D49; Console D, the mock's #/perf, #/perf/start and
// #/perf/hangs): the overview of one app, whose metric picker drives one daily chart, with the
// slowest screens, failing endpoints, hangs and frames by screen one click away; app start by
// type and breakdown with its histogram and MetricKit's daily reports; hangs and ANRs with the
// screens they happened on. Deltas compare the release in scope (the latest with every release)
// with the one before it (D103 #14); hang and ANR rates are per 1,000 sessions. Screens, network
// and frames are in Screens.tsx, Network.tsx and Frames.tsx.
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import {
  endpointQuery,
  endpointsQuery,
  framesQuery,
  hangsQuery,
  issuesQuery,
  metricKitQuery,
  screenQuery,
  screensQuery,
  seriesQuery,
  startQuery,
} from '../api/queries'
import type {
  App,
  EndpointSummary,
  Endpoints,
  Hangs,
  IssueSummary,
  PerformanceSeries,
  SeriesMetric,
  StartGroup,
} from '../api/types'
import {
  btnClass,
  EmptyNote,
  HovRow,
  LK,
  Pill,
  Section,
  Seg,
  SelectChip,
  Status,
  Strip,
  type StripItem,
  Table,
  TableFoot,
} from '../components/d'
import { LineChart } from '../components/LineChart'
import {
  ApproxFigure,
  Delta,
  deltaMeta,
  Histogram,
  IssueTable,
  LatencyText,
  NoApp,
  PerfHead,
  perfMeta,
  ReleaseLink,
  releaseMarker,
  usePerfReleases,
  usePerfScope,
  withRelease,
} from '../components/Perf'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { compact, duration, durationAxis, percentSpaced, perMinute } from '../lib/format'
import { issueTitle } from '../lib/issues'
import { type Latency, ttidAt } from '../lib/latency'
import { useProjectParams } from './params'
import { carried, START_GROUPS, START_TYPES, type StartSearch } from './search'

// ---------------------------------------------------------------- shared

const ms = (i18n: Translator, v: number | null | undefined) =>
  v === null || v === undefined ? '—' : duration(i18n, v)

/** A duration as a figure and its unit, for a strip cell: 980 ms, 1.92 s. */
export function splitMs(i18n: Translator, v: number | null | undefined): [string, string] {
  if (v === null || v === undefined) return ['—', '']
  if (v < 1000) return [i18n.number(Math.round(v)), 'ms']
  return [i18n.number(v / 1000, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 's']
}

/**
 * The strip's worst failure rate (APS-370): no request captured at all, requests that never
 * failed, or the endpoint failing the most with the rate over every endpoint.
 */
export function worstFailure(
  data: Endpoints | undefined,
):
  | { kind: 'none' }
  | { kind: 'ok' }
  | { kind: 'failing'; endpoint: EndpointSummary; rate: number; all: number | null } {
  if (!data || data.totals.requests === 0) return { kind: 'none' }
  const endpoint = data.endpoints.find((e) => (e.error_rate ?? 0) > 0)
  if (!endpoint) return { kind: 'ok' }
  return {
    kind: 'failing',
    endpoint,
    rate: endpoint.error_rate ?? 0,
    all: data.totals.error_rate,
  }
}

/** Sessions with a hang (or an ANR) per 1,000 sessions (D103 #14). */
export const perThousand = (affected: number, sessions: number): number | null =>
  sessions > 0 ? (affected / sessions) * 1000 : null

/** A rate per 1k: three significant digits (24.7, 25.0, 0.42). */
export const rate1k = (i18n: Translator, v: number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : i18n.number(
        v,
        Math.abs(v) >= 10
          ? { minimumSignificantDigits: 3, maximumSignificantDigits: 3 }
          : { maximumSignificantDigits: 3 },
      )

const dayLabel = (i18n: Translator, day: string) =>
  i18n.date(`${day}T00:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })

/** A series' value for one release (series by release). */
const atRelease = (s: PerformanceSeries | undefined, release: string | undefined) =>
  release === undefined ? null : (s?.points.find((p) => p.key === release)?.value ?? null)

/** The change between two values, when both exist. */
export const change = (a: number | null | undefined, b: number | null | undefined) =>
  a === null || a === undefined || b === null || b === undefined ? null : a - b

/** A hang's (or ANR's) counts for one release or the whole scope. */
export function hangCounts(h: Hangs | undefined, android: boolean, release?: string) {
  const rows = (h?.releases ?? []).filter((r) => release === undefined || r.release === release)
  const sessions = rows.reduce((n, r) => n + r.sessions, 0)
  const affected = rows.reduce(
    (n, r) => n + (android ? r.sessions_with_anr : r.sessions_with_hang),
    0,
  )
  return { sessions, affected, rate: perThousand(affected, sessions) }
}

function Page({ children }: { children: ReactNode }) {
  return (
    <main id="main" className="pg pf-pg">
      {children}
    </main>
  )
}

/** The common head of a view still loading its apps, or with no app. */
function Gate({
  title,
  page,
  appsPending,
  app,
}: {
  title: string
  page?: string
  appsPending: boolean
  app: App | undefined
}) {
  if (appsPending)
    return (
      <Page>
        <Loading />
      </Page>
    )
  if (!app)
    return (
      <Page>
        <PerfHead title={title} page={page} />
        <NoApp />
      </Page>
    )
  return null
}

/** A small mono link to an issue, with its state's glyph. */
function IssueRef({ issue }: { issue: IssueSummary }) {
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as StartSearch
  const s = issue.state?.status
  return (
    <>
      <Link
        className={LK}
        to="/o/$orgId/p/$projectId/issues/$issueId"
        params={{ orgId, projectId, issueId: issue.id }}
        search={carried(search)}
        translate="no"
        title={issueTitle(issue)}
      >
        {issue.id.slice(0, 8)}
      </Link>{' '}
      <Status
        s={
          s === 'resolved'
            ? 'res'
            : s === 'ignored'
              ? 'mute'
              : issue.state?.regressed_release
                ? 'reg'
                : 'on'
        }
      />
    </>
  )
}

// ---------------------------------------------------------------- overview

type Pick = 'cold' | 'screen' | 'fail' | 'hang' | 'frames'

/** Metrics the chart can show that have no cell of their own (the product's earlier picks). */
const OTHER: readonly SeriesMetric[] = [
  'ttid_p90',
  'network_p90',
  'frozen_frames_per_minute',
  'hang_rate',
  'anr_rate',
]

/** The longest window the API's per-release series takes (PERF_MAX_DAYS). */
const SERIES_DAYS = 30

/** Failures on an endpoint with fewer requests than this do not make it the worst. */
const MIN_REQUESTS = 20

export function PerformancePage() {
  const i18n = useI18n()
  const { t, number, rich } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const { scope, app, appsPending, search } = usePerfScope()
  const { list, current, previous } = usePerfReleases(scope)
  const [pick, setPick] = useState<Pick | SeriesMetric>('cold')
  const enabled = !!scope.app_id
  const android = app?.platform === 'android'
  const appId = scope.app_id ?? ''

  const start = useQuery({ ...startQuery(projectId, scope, 'none'), enabled })
  const screens = useQuery({ ...screensQuery(projectId, scope, 'ttfd_p90', 100), enabled })
  const screensBefore = useQuery({
    ...screensQuery(projectId, withRelease(scope, previous), 'ttfd_p90', 100),
    enabled: enabled && !!previous,
  })
  const failing = useQuery({
    ...endpointsQuery(projectId, scope, 'error_rate', 5, MIN_REQUESTS),
    enabled,
  })
  const hangs = useQuery({ ...hangsQuery(projectId, scope), enabled })
  const frames = useQuery({ ...framesQuery(projectId, scope, 'slow_per_minute', 3), enabled })
  const hangMetric: SeriesMetric = android ? 'anr_rate' : 'hang_rate'
  const coldByRelease = useQuery({
    ...seriesQuery(projectId, appId, 'cold_start_p90', SERIES_DAYS, 'release', scope),
    enabled: enabled && !!previous,
  })
  const hangByRelease = useQuery({
    ...seriesQuery(projectId, appId, hangMetric, SERIES_DAYS, 'release', scope),
    enabled: enabled && !!previous,
  })
  const slowByRelease = useQuery({
    ...seriesQuery(projectId, appId, 'slow_frames_per_minute', SERIES_DAYS, 'release', scope),
    enabled: enabled && !!previous,
  })

  const withTtfd = (screens.data ?? []).some((s) => s.ttfd)
  // TTFD when any screen reports it; else TTID, approximate from the rollups when no load's span
  // was released (D114, APS-445).
  const p90At = (s: (typeof screens.data & {})[number] | undefined): Latency | null =>
    withTtfd ? (s?.ttfd ? { ms: s.ttfd.p90_ms, approx: false } : null) : ttidAt(s, 'p90_ms')
  const p90Of = (s: (typeof screens.data & {})[number] | undefined) => p90At(s)?.ms ?? null
  const slowest = withTtfd
    ? screens.data?.[0]
    : [...(screens.data ?? [])].sort((a, b) => (p90Of(b) ?? -1) - (p90Of(a) ?? -1))[0]
  const before = (name: string | undefined) =>
    screensBefore.data?.find((s) => s.screen === name && s.app_id === appId)
  const fail = worstFailure(failing.data)
  const worst = fail.kind === 'failing' ? fail.endpoint : undefined
  // The slowest screen's daily TTID reads spans: none when it is known only from its rollups.
  const spanlessScreen = pick === 'screen' && !!p90At(slowest)?.approx
  const screenDays = useQuery({
    ...screenQuery(projectId, appId, slowest?.screen ?? '', scope),
    enabled: enabled && pick === 'screen' && !!slowest && !spanlessScreen,
  })
  const worstDays = useQuery({
    ...endpointQuery(
      projectId,
      appId,
      { method: worst?.method ?? '', host: worst?.host ?? '', template: worst?.template ?? '' },
      scope,
    ),
    enabled: enabled && pick === 'fail' && !!worst,
  })
  const dailyMetric: SeriesMetric | undefined =
    pick === 'cold'
      ? 'cold_start_p90'
      : pick === 'hang'
        ? hangMetric
        : pick === 'frames'
          ? 'slow_frames_per_minute'
          : pick === 'screen' || pick === 'fail'
            ? undefined
            : pick
  const daily = useQuery({
    ...seriesQuery(projectId, appId, dailyMetric ?? 'cold_start_p90', scope.days, 'day', scope),
    enabled: enabled && !!dailyMetric,
  })
  const hangScreens = (hangs.data?.screens ?? [])
    .filter((s) => (android ? s.anrs : s.hangs) > 0)
    .sort((a, b) => (android ? b.anrs - a.anrs : b.hangs - a.hangs))
    .slice(0, 3)
  const hangKind = android ? ('anr' as const) : ('hang' as const)
  // The most widespread hang issue on each of those screens (APS-304's screen filter).
  const topIssue = useQueries({
    queries: hangScreens.map((s) => ({
      ...issuesQuery(projectId, {
        app_id: scope.app_id,
        release: scope.release,
        kind: hangKind,
        screen: s.screen,
        days: scope.days,
        limit: 1,
        sort: 'users',
      }),
      enabled: enabled && !!s.screen,
    })),
  })

  const gate = <Gate title={t('nav.performance')} appsPending={appsPending} app={app} />
  if (appsPending || !app) return gate

  const params = { orgId, projectId }
  const keep = { ...carried(search), app: scope.app_id }
  const fmtMs = (v: number) => duration(i18n, v)
  const loadsOf = new Map((screens.data ?? []).map((s) => [s.screen, s.loads]))
  const maxPer1k = Math.max(
    -1,
    ...hangScreens.map((s) => {
      const loads = loadsOf.get(s.screen)
      return loads ? ((android ? s.anrs : s.hangs) / loads) * 1000 : -1
    }),
  )

  // the five cells
  const cold = start.data?.groups.find((g) => g.start_type === 'cold')
  const coldD = change(
    atRelease(coldByRelease.data, current),
    atRelease(coldByRelease.data, previous),
  )
  const hc = hangCounts(hangs.data, android)
  const hangD = change(
    atRelease(hangByRelease.data, current),
    atRelease(hangByRelease.data, previous),
  )
  const slowD = change(
    atRelease(slowByRelease.data, current),
    atRelease(slowByRelease.data, previous),
  )
  const screenD = change(p90Of(slowest), p90Of(before(slowest?.screen)))
  const slowPerMin = frames.data?.totals.slow_per_minute ?? null
  const [coldV, coldU] = splitMs(i18n, cold?.p90_ms)
  const [scrV, scrU] = splitMs(i18n, p90Of(slowest))
  const pending = (q: { isPending: boolean }, v: string) => (q.isPending ? '…' : v)
  const line = (...parts: ReactNode[]) => (
    <>
      {parts
        .filter((p) => p !== null && p !== undefined && p !== false && p !== '')
        .map((p, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional parts of one line
          <span key={i}>
            {i > 0 ? ' · ' : ''}
            {p}
          </span>
        ))}
    </>
  )
  // The head's meta names the pair the deltas compare, so a cell carries the delta alone.
  const withVs = (d: ReactNode) => (previous ? d : null)
  const cells: { key: Pick; dt: string; dd: ReactNode; small: string; d: ReactNode }[] = [
    {
      key: 'cold',
      dt: t('perf.card.coldP90'),
      dd: pending(start, coldV),
      small: coldU,
      // Nothing under the '…' while the read runs: a slow read never reads as no data (APS-423).
      d: start.isPending
        ? null
        : line(
            withVs(<Delta value={coldD} format={fmtMs} />),
            cold ? t('perf.card.p50', { value: ms(i18n, cold.p50_ms) }) : t('perf.card.none'),
          ),
    },
    {
      key: 'screen',
      dt: withTtfd ? t('perf.card.screenTtfd') : t('perf.card.screen'),
      dd: p90At(slowest)?.approx ? <ApproxFigure>{scrV}</ApproxFigure> : pending(screens, scrV),
      small: scrU,
      d: screens.isPending
        ? null
        : line(
            withVs(<Delta value={screenD} format={fmtMs} />),
            slowest ? <span translate="no">{slowest.screen}</span> : t('perf.card.none'),
          ),
    },
    {
      key: 'fail',
      dt: t('perf.card.worstFail'),
      // A dash only when no request was captured; requests that never failed read 0 % (APS-370).
      dd: pending(
        failing,
        fail.kind === 'failing'
          ? i18n.number(fail.rate * 100, { maximumFractionDigits: 1 })
          : fail.kind === 'ok'
            ? '0'
            : '—',
      ),
      // no unit beside a dash
      small: failing.isPending || fail.kind === 'none' ? '' : '%',
      d: failing.isPending
        ? null
        : fail.kind === 'none'
          ? t('perf.card.noRequests')
          : fail.kind === 'ok'
            ? t('perf.card.noFailures')
            : line(
                <span translate="no">{`${fail.endpoint.method} ${fail.endpoint.template}`}</span>,
                fail.all === null
                  ? null
                  : t('perf.card.allFailed', { rate: percentSpaced(i18n, fail.all, 2) }),
              ),
    },
    {
      key: 'hang',
      dt: android ? t('perf.card.anrRate') : t('perf.card.hangRate'),
      dd: pending(hangs, rate1k(i18n, hc.rate)),
      // no unit beside a dash (no session in scope)
      small: hc.rate === null ? '' : '/1k',
      d: hangs.isPending
        ? null
        : hc.rate === null
          ? t('perf.card.noSessions')
          : line(
              withVs(
                <Delta
                  value={hangD === null ? null : hangD * 1000}
                  format={(v) => rate1k(i18n, v)}
                />,
              ),
              t('perf.card.affected', {
                count: hc.affected,
                total: hc.sessions,
                sessions: compact(i18n, hc.sessions),
              }),
            ),
    },
    {
      key: 'frames',
      dt: t('perf.card.slowFrames'),
      dd: pending(frames, slowPerMin === null ? '—' : perMinute(i18n, slowPerMin)),
      // A dash has no unit and no delta (APS-370, APS-409): the series by release can hold
      // values while the scope has no frames.
      small: slowPerMin === null ? '' : '/min',
      d: frames.isPending
        ? null
        : slowPerMin === null
          ? frames.isError
            ? null
            : t('perf.card.noFrames')
          : line(
              withVs(<Delta value={slowD} format={(v) => perMinute(i18n, v)} />),
              frames.data?.totals.frozen_per_minute !== null &&
                frames.data?.totals.frozen_per_minute !== undefined
                ? t('perf.card.frozen', {
                    value: perMinute(i18n, frames.data.totals.frozen_per_minute),
                  })
                : null,
            ),
    },
  ]

  // the chart for the pick
  const isCell = cells.some((c) => c.key === pick)
  const caption = isCell
    ? t(`perf.cap.${pick as Pick}`, {
        screen: slowest?.screen ?? '',
        endpoint: worst ? `${worst.method} ${worst.template}` : '',
        ttfd: withTtfd ? 1 : 0,
        anr: android ? 1 : 0,
      })
    : t(`perf.trend.${pick as SeriesMetric}`)
  const unit =
    pick === 'hang' || pick === 'hang_rate' || pick === 'anr_rate'
      ? '/1k'
      : pick === 'fail'
        ? '%'
        : pick === 'frames' || pick === 'frozen_frames_per_minute'
          ? '/min'
          : undefined
  type Pt = { label: string; value: number | null }
  let points: Pt[] | undefined
  let days: string[] = []
  let chartQuery: { isPending: boolean; isError: boolean; error: unknown; refetch: () => void } =
    daily
  if (pick === 'screen') {
    chartQuery = screenDays
    days = screenDays.data?.days.map((d) => d.day) ?? []
    points = screenDays.data?.days.map((d) => ({
      label: dayLabel(i18n, d.day),
      value: withTtfd ? d.ttfd_p90_ms : d.ttid_p90_ms,
    }))
  } else if (pick === 'fail') {
    chartQuery = worstDays
    days = worstDays.data?.days.map((d) => d.day) ?? []
    points = worstDays.data?.days.map((d) => ({
      label: dayLabel(i18n, d.day),
      value: d.error_rate === null ? null : d.error_rate * 100,
    }))
  } else if (dailyMetric) {
    const per1k = dailyMetric === 'hang_rate' || dailyMetric === 'anr_rate'
    const pct = dailyMetric === 'network_error_rate'
    days = daily.data?.points.map((p) => p.key) ?? []
    points = daily.data?.points.map((p) => ({
      label: dayLabel(i18n, p.key),
      value: p.value === null ? null : per1k ? p.value * 1000 : pct ? p.value * 100 : p.value,
    }))
  }
  // The unit rides on the value, so the axis prints it on its top tick only (LineChart). A
  // duration reads as the shared formatter writes it: seconds from 1 s, ms below (APS-408).
  const msAxis = durationAxis(i18n, Math.max(0, ...(points ?? []).map((p) => p.value ?? 0)))
  const format = (v: number) =>
    unit === '/1k'
      ? `${rate1k(i18n, v)} /1k`
      : unit === '%'
        ? `${i18n.number(v, { maximumFractionDigits: 1 })} %`
        : unit === '/min'
          ? `${perMinute(i18n, v)} /min`
          : msAxis(v)
  const ghost = btnClass('ghost', 'sm')
  const arrow = (label: string) => (
    <>
      <span translate="no">{label}</span> →
    </>
  )
  // The page the chart's metric comes from.
  const goPage =
    pick === 'cold' ? (
      <Link
        className={ghost}
        to="/o/$orgId/p/$projectId/performance/start"
        params={params}
        search={keep}
      >
        {arrow(t('perf.nav.start'))}
      </Link>
    ) : pick === 'screen' && slowest ? (
      <Link
        className={ghost}
        to="/o/$orgId/p/$projectId/performance/screens/detail"
        params={params}
        search={{ ...keep, screen: slowest.screen }}
      >
        {arrow(slowest.screen)}
      </Link>
    ) : pick === 'fail' && worst ? (
      <Link
        className={ghost}
        to="/o/$orgId/p/$projectId/performance/network/endpoint"
        params={params}
        search={{ ...keep, method: worst.method, host: worst.host, template: worst.template }}
      >
        {arrow(`${worst.method} ${worst.template}`)}
      </Link>
    ) : pick === 'hang' ? (
      <Link
        className={ghost}
        to="/o/$orgId/p/$projectId/performance/hangs"
        params={params}
        search={keep}
      >
        {arrow(t('perf.nav.hangs'))}
      </Link>
    ) : pick === 'frames' ? (
      <Link
        className={ghost}
        to="/o/$orgId/p/$projectId/performance/frames"
        params={params}
        search={keep}
      >
        {arrow(t('perf.nav.frames'))}
      </Link>
    ) : null
  const otherMetrics = OTHER.filter((m) => m !== hangMetric)

  const meta = perfMeta(i18n, app, scope, [
    t('perf.meta.reweighted'),
    deltaMeta(t, scope, current, previous),
  ])

  return (
    <Page>
      <PerfHead
        title={t('nav.performance')}
        meta={meta}
        actions={
          current && previous ? (
            <Link
              className={btnClass()}
              to="/o/$orgId/p/$projectId/releases"
              params={params}
              search={{ ...keep, release: undefined, cand: current, base: previous }}
            >
              {t('perf.compare', { current, previous })}
            </Link>
          ) : undefined
        }
      />
      <section className="sec pf-trend" aria-label={t('perf.trend.title')}>
        {/* biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a fieldset */}
        <div className="metric-pick" role="group" aria-label={t('perf.trend.pick')}>
          {cells.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={pick === c.key}
              onClick={() => setPick(c.key)}
            >
              <dl>
                <dt>{c.dt}</dt>
                <dd translate="no">
                  {c.dd}
                  {c.small ? <small> {c.small}</small> : null}
                </dd>
                <dd className="d">{c.d}</dd>
              </dl>
            </button>
          ))}
        </div>
        <div className="pf-cap">
          <span>
            <b>{caption}</b>
            {`, ${t('perf.cap.per', {
              days: scope.days,
              release: scope.release ?? t('perf.meta.allReleases'),
            })}`}
          </span>
          <span className="r">
            <SelectChip
              label={t('perf.trend.other')}
              value={isCell ? '' : pick}
              onChange={(v) => setPick(v ? (v as SeriesMetric) : 'cold')}
            >
              <option value="">{t('perf.trend.other')}</option>
              {otherMetrics.map((m) => (
                <option key={m} value={m}>
                  {t(`perf.trend.${m}`)}
                </option>
              ))}
            </SelectChip>
            {goPage}
          </span>
        </div>
        <div className="pf-chart">
          {spanlessScreen ? (
            <EmptyNote title={t('perf.approx.spans')}>{t('perf.approx.spansBody')}</EmptyNote>
          ) : chartQuery.isPending ? (
            <Loading />
          ) : chartQuery.isError ? (
            <ErrorState error={chartQuery.error} retry={() => chartQuery.refetch()} />
          ) : !points || points.every((p) => p.value === null) ? (
            <EmptyNote title={t('perf.trend.empty')} />
          ) : (
            <LineChart
              label={t('perf.trend.label', { metric: caption, days: scope.days })}
              points={points}
              // Durations (no unit: ms) start at 0; rates keep their own scale (APS-408).
              fromZero={unit === undefined}
              format={format}
              marker={releaseMarker(list, current, days)}
              accent
              timeAxis
              xName={t('chart.day')}
            />
          )}
        </div>
      </section>

      <div className="grid-h">
        <Section
          title={t('perf.slowest.title')}
          meta={withTtfd ? 'TTFD p90' : 'TTID p90'}
          right={
            <Link
              className={btnClass('ghost', 'sm')}
              to="/o/$orgId/p/$projectId/performance/screens"
              params={params}
              search={keep}
            >
              {t('perf.slowest.all')}
            </Link>
          }
        >
          {screens.isPending ? (
            <Loading />
          ) : screens.isError ? (
            <ErrorState error={screens.error} retry={() => screens.refetch()} />
          ) : (screens.data ?? []).length === 0 ? (
            <EmptyNote title={t('perf.screens.none')}>{rich('perf.screens.noneBody')}</EmptyNote>
          ) : (
            <Table label={t('table.of', { name: t('perf.slowest.title') })} className="pf-tbl">
              <thead>
                <tr>
                  <th scope="col">{t('vitals.col.screen')}</th>
                  <th scope="col" className="num">
                    {t('perf.col.loads')}
                  </th>
                  <th scope="col" className="num" translate="no">
                    {withTtfd ? 'TTFD p90' : 'TTID p90'}
                  </th>
                  <th scope="col" className="num">
                    {previous ? t('perf.delta.vs', { release: previous }) : t('perf.col.change')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...(screens.data ?? [])]
                  .sort((a, b) => (p90Of(b) ?? -1) - (p90Of(a) ?? -1))
                  .slice(0, 3)
                  .map((s, i) => {
                    const d = change(p90Of(s), p90Of(before(s.screen)))
                    return (
                      <HovRow
                        key={`${s.app_id} ${s.screen}`}
                        onOpen={() =>
                          navigate({
                            to: '/o/$orgId/p/$projectId/performance/screens/detail',
                            params,
                            search: { ...keep, screen: s.screen },
                          })
                        }
                      >
                        <td className="mono anywhere" translate="no">
                          <Link
                            to="/o/$orgId/p/$projectId/performance/screens/detail"
                            params={params}
                            search={{ ...keep, screen: s.screen }}
                          >
                            {s.screen}
                          </Link>
                        </td>
                        <td className="num">{compact(i18n, s.loads)}</td>
                        <td className="num">
                          {i === 0 && (d ?? 0) > 0 ? (
                            <Pill tone="warn">
                              <LatencyText v={p90At(s)} />
                            </Pill>
                          ) : (
                            <LatencyText v={p90At(s)} />
                          )}
                        </td>
                        <td className="num">
                          <Delta value={d} format={fmtMs} />
                        </td>
                      </HovRow>
                    )
                  })}
              </tbody>
            </Table>
          )}
        </Section>
        <Section
          title={t('perf.failing.title')}
          meta={t('perf.failing.meta')}
          right={
            <Link
              className={btnClass('ghost', 'sm')}
              to="/o/$orgId/p/$projectId/performance/network"
              params={params}
              search={{ ...keep, sort: 'error_rate' }}
            >
              {t('perf.failing.all')}
            </Link>
          }
        >
          {failing.isPending ? (
            <Loading />
          ) : failing.isError ? (
            <ErrorState error={failing.error} retry={() => failing.refetch()} />
          ) : failing.data.endpoints.filter((e) => (e.error_rate ?? 0) > 0).length === 0 ? (
            <EmptyNote title={t('perf.failing.none')} />
          ) : (
            <Table label={t('table.of', { name: t('perf.failing.title') })} className="pf-tbl">
              <thead>
                <tr>
                  <th scope="col">{t('perf.col.endpoint')}</th>
                  <th scope="col" className="num">
                    {t('perf.col.failed')}
                  </th>
                  <th scope="col" className="num">
                    {t('perf.col.ofRequests')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {failing.data.endpoints
                  .filter((e) => (e.error_rate ?? 0) > 0)
                  .slice(0, 3)
                  .map((e, i) => {
                    const search = {
                      ...keep,
                      method: e.method,
                      host: e.host,
                      template: e.template,
                    }
                    const failed = Math.round((e.error_rate ?? 0) * e.requests)
                    return (
                      <HovRow
                        key={`${e.method} ${e.host} ${e.template}`}
                        onOpen={() =>
                          navigate({
                            to: '/o/$orgId/p/$projectId/performance/network/endpoint',
                            params,
                            search,
                          })
                        }
                      >
                        <td className="mono anywhere" translate="no">
                          <Link
                            to="/o/$orgId/p/$projectId/performance/network/endpoint"
                            params={params}
                            search={search}
                          >
                            {`${e.method} ${e.template}`}
                          </Link>
                        </td>
                        <td className="num">
                          {i === 0 ? (
                            <Pill tone="crash">{i18n.percent(e.error_rate ?? 0, 1)}</Pill>
                          ) : (
                            i18n.percent(e.error_rate ?? 0, 1)
                          )}
                        </td>
                        <td className="num">
                          {t('perf.failing.of', {
                            failed: compact(i18n, failed),
                            requests: compact(i18n, e.requests),
                          })}
                        </td>
                      </HovRow>
                    )
                  })}
              </tbody>
            </Table>
          )}
        </Section>
      </div>

      <div className="grid-h">
        <Section
          title={android ? t('perf.byScreen.anrs') : t('perf.byScreen.hangs')}
          meta={t(android ? 'perf.byScreen.anrCount' : 'perf.byScreen.hangCount', {
            count: (hangs.data?.screens ?? []).reduce(
              (n, s) => n + (android ? s.anrs : s.hangs),
              0,
            ),
          })}
          right={
            <Link
              className={btnClass('ghost', 'sm')}
              to="/o/$orgId/p/$projectId/performance/hangs"
              params={params}
              search={keep}
            >
              {t('perf.nav.hangs')}
            </Link>
          }
        >
          {hangs.isPending ? (
            <Loading />
          ) : hangs.isError ? (
            <ErrorState error={hangs.error} retry={() => hangs.refetch()} />
          ) : hangScreens.length === 0 ? (
            <EmptyNote title={t('perf.hangs.noneOnScreens')} />
          ) : (
            <Table
              label={t('table.of', {
                name: android ? t('perf.byScreen.anrs') : t('perf.byScreen.hangs'),
              })}
              className="pf-tbl"
            >
              <thead>
                <tr>
                  <th scope="col">{t('vitals.col.screen')}</th>
                  <th scope="col" className="num">
                    {android ? t('perf.col.anrs') : t('perf.col.hangs')}
                  </th>
                  <th scope="col" className="num">
                    {t('perf.col.per1kLoads')}
                  </th>
                  {android ? null : (
                    <th scope="col" className="num">
                      {t('perf.col.p90Duration')}
                    </th>
                  )}
                  <th scope="col">{t('perf.col.topIssue')}</th>
                </tr>
              </thead>
              <tbody>
                {hangScreens.map((s, i) => {
                  const n = android ? s.anrs : s.hangs
                  const loads = loadsOf.get(s.screen)
                  const per1k = loads ? (n / loads) * 1000 : null
                  const issue = topIssue[i]?.data?.issues[0]
                  return (
                    <HovRow
                      key={`${s.app_id} ${s.screen}`}
                      onOpen={() =>
                        navigate({
                          to: '/o/$orgId/p/$projectId/performance/hangs',
                          params,
                          search: keep,
                        })
                      }
                    >
                      <td className="mono anywhere" translate="no">
                        {s.screen || <span className="dim">{t('perf.hangs.noScreen')}</span>}
                      </td>
                      <td className="num">{number(n)}</td>
                      <td className="num">
                        {per1k === null ? (
                          <span className="faint">—</span>
                        ) : per1k === maxPer1k ? (
                          <Pill tone="warn">{rate1k(i18n, per1k)}</Pill>
                        ) : (
                          rate1k(i18n, per1k)
                        )}
                      </td>
                      {android ? null : <td className="num">{ms(i18n, s.p90_ms)}</td>}
                      <td>
                        {issue ? <IssueRef issue={issue} /> : <span className="faint">—</span>}
                      </td>
                    </HovRow>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Section>
        <Section
          title={t('perf.byScreen.frames')}
          meta={t('perf.byScreen.framesMeta')}
          right={
            <Link
              className={btnClass('ghost', 'sm')}
              to="/o/$orgId/p/$projectId/performance/frames"
              params={params}
              search={keep}
            >
              {t('perf.nav.frames')}
            </Link>
          }
        >
          {frames.isPending ? (
            <Loading />
          ) : frames.isError ? (
            <ErrorState error={frames.error} retry={() => frames.refetch()} />
          ) : frames.data.screens.filter((s) => s.screen).length === 0 ? (
            <EmptyNote title={t('perf.frames.none')} />
          ) : (
            <Table label={t('table.of', { name: t('perf.byScreen.frames') })} className="pf-tbl">
              <thead>
                <tr>
                  <th scope="col">{t('vitals.col.screen')}</th>
                  <th scope="col" className="num">
                    {t('perf.frames.col.slow')}
                  </th>
                  <th scope="col" className="num">
                    {t('perf.frames.col.frozen')}
                  </th>
                  <th scope="col" className="num">
                    {t('perf.col.foreground')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {frames.data.screens
                  .filter((s) => s.screen)
                  .slice(0, 3)
                  .map((s, i) => (
                    <HovRow
                      key={`${s.app_id} ${s.screen}`}
                      onOpen={() =>
                        navigate({
                          to: '/o/$orgId/p/$projectId/performance/frames',
                          params,
                          search: keep,
                        })
                      }
                    >
                      <td className="mono anywhere" translate="no">
                        {s.screen}
                      </td>
                      <td className="num">
                        {i === 0 ? (
                          <Pill tone="warn">{perMinute(i18n, s.slow_per_minute ?? 0)}</Pill>
                        ) : (
                          perMinute(i18n, s.slow_per_minute ?? 0)
                        )}
                      </td>
                      <td className="num">{perMinute(i18n, s.frozen_per_minute ?? 0)}</td>
                      <td className="num" translate="no">
                        {`${i18n.number(s.foreground_minutes / 60, { maximumFractionDigits: 1 })} h`}
                      </td>
                    </HovRow>
                  ))}
              </tbody>
            </Table>
          )}
        </Section>
      </div>
    </Page>
  )
}

// ---------------------------------------------------------------- app start

type StartType = (typeof START_TYPES)[number]
type StartBy = (typeof START_GROUPS)[number]

/** The type's word: Cold, Warm, Hot (never "Cold start p90", the old label's bug). */
const TYPE_KEY = {
  cold: 'perf.start.type.cold',
  warm: 'perf.start.type.warm',
  hot: 'perf.start.type.hot',
} as const satisfies Record<StartType, MessageId>

const GROUP_KEY = {
  release: 'perf.start.by.release',
  os_version: 'perf.start.by.os',
  device_model: 'perf.start.by.device',
  prewarmed: 'perf.start.by.prewarmed',
} as const satisfies Record<StartBy, MessageId>

export function StartPage() {
  const i18n = useI18n()
  const { t, rich } = i18n
  const { projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as StartSearch
  const { scope, app, appsPending } = usePerfScope()
  const { list, current, previous } = usePerfReleases(scope)
  const ios = app?.platform === 'ios'
  const by: StartBy = search.by === 'prewarmed' && !ios ? 'release' : (search.by ?? 'release')
  const type: StartType = search.type ?? 'cold'
  const enabled = !!scope.app_id
  const types = useQuery({ ...startQuery(projectId, scope, 'none'), enabled })
  // Every release, for the strip's deltas whatever the scope (D103 #14).
  const releases = useQuery({
    ...startQuery(projectId, withRelease(scope, undefined), 'release'),
    enabled,
  })
  // With every release in scope, the strip shows the latest release, as its histogram does.
  const stripScope = scope.release ? scope : withRelease(scope, current)
  const prewarm = useQuery({
    ...startQuery(projectId, stripScope, 'prewarmed'),
    enabled: enabled && ios && !!stripScope.release,
  })
  // The types table reads the strip's release too, as the mock does.
  const typesNow = useQuery({
    ...startQuery(projectId, stripScope, 'none'),
    enabled: enabled && !!stripScope.release,
  })
  const breakdown = useQuery({ ...startQuery(projectId, scope, by, true), enabled })
  const breakdownBefore = useQuery({
    ...startQuery(projectId, withRelease(scope, previous), by),
    enabled: enabled && by !== 'release' && !!previous,
  })
  const metrickit = useQuery({
    ...metricKitQuery(projectId, scope.app_id ?? '', scope.days, 'time_to_first_draw', scope),
    enabled: enabled && ios,
  })
  const [picked, setPicked] = useState<string | undefined>()

  const title = t('perf.start.title')
  const page = t('perf.nav.start')
  if (appsPending || !app)
    return <Gate title={title} page={page} appsPending={appsPending} app={app} />

  const set = (patch: Partial<StartSearch>) =>
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
    })
  const label = (key: string) =>
    by === 'prewarmed'
      ? key === 'prewarmed'
        ? t('perf.start.prewarmed')
        : t('perf.start.notPrewarmed')
      : key || '—'
  const fmtMs = (v: number) => duration(i18n, v)
  const typeName = t(TYPE_KEY[type])

  // the strip: cold starts in scope, and the compared release against the one before it
  const coldOf = (rows: StartGroup[] | undefined, release?: string) =>
    rows?.find((g) => g.start_type === 'cold' && (release === undefined || g.key === release))
  const coldNow = coldOf(releases.data?.groups, current)
  const cold = scope.release ? coldOf(types.data?.groups) : coldNow
  const coldWas = coldOf(releases.data?.groups, previous)
  const total = (types.data?.groups ?? []).reduce((n, g) => n + g.count, 0)
  const pw = (prewarm.data?.groups ?? []).filter((g) => g.start_type === 'cold')
  const pwShare = (() => {
    const all = pw.reduce((n, g) => n + g.count, 0)
    const yes = pw.find((g) => g.key === 'prewarmed')?.count ?? 0
    return all > 0 ? yes / all : null
  })()
  const [p50v, p50u] = splitMs(i18n, cold?.p50_ms)
  const [p90v, p90u] = splitMs(i18n, cold?.p90_ms)
  const strip: StripItem[] = [
    {
      dt: t('perf.start.coldP50'),
      dd: p50v,
      small: p50u,
      d: (
        <>
          {previous ? (
            <>
              <Delta value={change(coldNow?.p50_ms, coldWas?.p50_ms)} format={fmtMs} /> ·{' '}
            </>
          ) : null}
          {t('perf.start.starts', { count: cold?.count ?? 0, n: compact(i18n, cold?.count ?? 0) })}
        </>
      ),
    },
    {
      dt: t('perf.start.coldP90'),
      dd: p90v,
      small: p90u,
      d: previous ? (
        <>
          <Delta value={change(coldNow?.p90_ms, coldWas?.p90_ms)} format={fmtMs} /> ·{' '}
          {t('perf.start.was', { value: ms(i18n, coldWas?.p90_ms), release: previous })}
        </>
      ) : (
        t('perf.card.p50', { value: ms(i18n, cold?.p50_ms) })
      ),
    },
    ...(ios
      ? [
          {
            dt: t('perf.start.prewarmed'),
            dd: pwShare === null ? '—' : i18n.number(pwShare * 100, { maximumFractionDigits: 0 }),
            small: '%',
            d: t('perf.start.prewarmedLine'),
          },
        ]
      : []),
  ]

  // the breakdown: one type's groups, the selected one drives the histogram
  const order = new Map(list.map((r, i) => [r.release, i]))
  const groups = (breakdown.data?.groups ?? [])
    .filter((g) => g.start_type === type)
    // Releases newest first, as the releases list orders them.
    .sort((a, b) => (by === 'release' ? (order.get(a.key) ?? 1e9) - (order.get(b.key) ?? 1e9) : 0))
  const selected = groups.find((g) => g.key === picked) ?? groups[0]
  const prewarmOnly = by === 'prewarmed' && type !== 'cold'
  const releaseOrder = groups.map((g) => g.key)
  const changeOf = (g: StartGroup): number | null => {
    if (by === 'release') {
      // each row is a release: the next row down is the one before it
      const next = releaseOrder[releaseOrder.indexOf(g.key) + 1]
      const was = groups.find((x) => x.key === next)
      return was ? g.p90_ms - was.p90_ms : null
    }
    const was = breakdownBefore.data?.groups.find((x) => x.start_type === type && x.key === g.key)
    return was ? g.p90_ms - was.p90_ms : null
  }
  const breakdownMeta =
    by === 'release'
      ? t('perf.start.metaByRelease')
      : previous
        ? t('perf.start.metaVs', {
            release: scope.release ?? t('perf.meta.allReleases'),
            previous,
          })
        : (scope.release ?? t('perf.meta.allReleases'))

  return (
    <Page>
      <PerfHead
        title={title}
        page={page}
        meta={perfMeta(i18n, app, scope, [
          total > 0 ? t('perf.start.metaStarts', { n: compact(i18n, total), count: total }) : null,
          'app.start',
          !scope.release && current ? t('perf.start.metaFigures', { release: current }) : null,
          deltaMeta(t, scope, current, previous),
        ])}
      />
      {types.isPending ? (
        <Loading />
      ) : types.isError ? (
        <ErrorState error={types.error} retry={() => types.refetch()} />
      ) : types.data.groups.length === 0 ? (
        <EmptyNote title={t('perf.start.none')}>{rich('perf.start.noneBody')}</EmptyNote>
      ) : (
        <>
          <Strip items={strip} translate="no" />
          <div className="grid2">
            <Section
              title={t('perf.start.histTitle', { type: typeName })}
              meta={
                !selected
                  ? undefined
                  : // A group without a name says only its count, never a lone dash (APS-399).
                    by !== 'prewarmed' && !selected.key
                    ? t('perf.start.histMetaCount', {
                        count: selected.count,
                        n: compact(i18n, selected.count),
                      })
                    : t('perf.start.histMeta', {
                        group: label(selected.key),
                        count: selected.count,
                        n: compact(i18n, selected.count),
                      })
              }
            >
              <div className="pf-chart">
                {breakdown.isPending ? (
                  <Loading />
                ) : selected && !prewarmOnly ? (
                  <Histogram
                    data={selected.histogram}
                    fine={selected.fine_histogram}
                    p50={selected.p50_ms}
                    p90={selected.p90_ms}
                    countName={(count) => t('chart.starts', { count })}
                    label={t('perf.start.histogramLabel', {
                      type: typeName,
                      group: label(selected.key),
                    })}
                  />
                ) : (
                  <EmptyNote title={t('perf.start.none')} />
                )}
              </div>
            </Section>
            <Section
              title={<span title={t('perf.start.lede')}>{t('perf.start.types')}</span>}
              meta={stripScope.release ?? t('perf.meta.allReleases')}
            >
              {stripScope.release && typesNow.isPending ? (
                <Loading />
              ) : (
                <Table label={t('table.of', { name: t('perf.start.types') })} className="pf-tbl">
                  <thead>
                    <tr>
                      <th scope="col">{t('perf.col.type')}</th>
                      <th scope="col" className="num">
                        {t('perf.col.count')}
                      </th>
                      <th scope="col" className="num" translate="no">
                        p50
                      </th>
                      <th scope="col" className="num" translate="no">
                        p90
                      </th>
                      <th scope="col" className="num" translate="no">
                        p95
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {START_TYPES.map((k) => {
                      const g = (
                        stripScope.release ? typesNow.data?.groups : types.data.groups
                      )?.find((x) => x.start_type === k)
                      return g ? (
                        <tr key={k}>
                          <td>{t(TYPE_KEY[k])}</td>
                          <td className="num">{compact(i18n, g.count)}</td>
                          <td className="num">{ms(i18n, g.p50_ms)}</td>
                          <td className="num">{ms(i18n, g.p90_ms)}</td>
                          <td className="num">{ms(i18n, g.p95_ms)}</td>
                        </tr>
                      ) : null
                    })}
                  </tbody>
                </Table>
              )}
            </Section>
          </div>
          <Section
            title={t('perf.start.byTitle', {
              type: typeName,
              group: t(GROUP_KEY[by]).toLowerCase(),
            })}
            meta={breakdownMeta}
          >
            <div className="pf-ctl">
              <Seg
                label={t('perf.start.type')}
                options={START_TYPES.map((k) => ({ value: k, label: t(TYPE_KEY[k]) }))}
                value={type}
                onChange={(v) => {
                  setPicked(undefined)
                  set({ type: v === 'cold' ? undefined : v })
                }}
              />
              <Seg
                label={t('perf.start.by')}
                options={START_GROUPS.filter((g) => g !== 'prewarmed' || ios).map((g) => ({
                  value: g,
                  label: t(GROUP_KEY[g]),
                }))}
                value={by}
                onChange={(v) => {
                  setPicked(undefined)
                  set({ by: v === 'release' ? undefined : v })
                }}
              />
            </div>
            {prewarmOnly ? (
              <EmptyNote title={t('perf.start.prewarmColdOnly')}>
                {t('perf.start.prewarmColdOnlyBody')}
              </EmptyNote>
            ) : breakdown.isPending ? (
              <Loading />
            ) : breakdown.isError ? (
              <ErrorState error={breakdown.error} retry={() => breakdown.refetch()} />
            ) : groups.length === 0 ? (
              <EmptyNote title={t('perf.start.none')}>{rich('perf.start.noneBody')}</EmptyNote>
            ) : (
              <Table
                label={t('table.of', {
                  name: t('perf.start.byTitle', { type: typeName, group: t(GROUP_KEY[by]) }),
                })}
                rowKeys
                className="pf-tbl"
              >
                <thead>
                  <tr>
                    <th scope="col">{t(GROUP_KEY[by])}</th>
                    <th scope="col" className="num">
                      {t('perf.col.count')}
                    </th>
                    <th scope="col" className="num" translate="no">
                      p50
                    </th>
                    <th scope="col" className="num" translate="no">
                      p90
                    </th>
                    <th scope="col" className="num" translate="no">
                      p95
                    </th>
                    <th scope="col" className="num">
                      {t('perf.col.change')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const d = changeOf(g)
                    return (
                      <HovRow
                        key={g.key}
                        cur={g === selected}
                        label={t('perf.start.pick', { group: label(g.key) })}
                        onOpen={() => setPicked(g.key)}
                      >
                        <td className="mono anywhere" translate="no">
                          {by === 'release' ? <ReleaseLink release={g.key} /> : label(g.key)}
                        </td>
                        <td className="num">{compact(i18n, g.count)}</td>
                        <td className="num">{ms(i18n, g.p50_ms)}</td>
                        <td className="num">{ms(i18n, g.p90_ms)}</td>
                        <td className="num">{ms(i18n, g.p95_ms)}</td>
                        <td className="num">
                          {d === null && by === 'os_version' && previous ? (
                            <span className="faint">{t('perf.start.new')}</span>
                          ) : (
                            <Delta value={d} format={fmtMs} />
                          )}
                        </td>
                      </HovRow>
                    )
                  })}
                </tbody>
              </Table>
            )}
            {ios ? <p className="pf-note">{t('perf.start.prewarmNote')}</p> : null}
          </Section>
          {ios ? (
            <Section title={t('perf.metrickit.title')} meta={t('perf.metrickit.meta')}>
              {metrickit.isPending ? (
                <Loading />
              ) : metrickit.isError ? (
                <ErrorState error={metrickit.error} retry={() => metrickit.refetch()} />
              ) : metrickit.data.groups.length === 0 ? (
                <EmptyNote title={t('perf.metrickit.none')}>
                  {t('perf.metrickit.noneBody')}
                </EmptyNote>
              ) : (
                <Table
                  label={t('table.of', { name: t('perf.metrickit.title') })}
                  className="pf-tbl"
                >
                  <thead>
                    <tr>
                      <th scope="col">{t('releases.col.release')}</th>
                      <th scope="col" className="num">
                        {t('perf.metrickit.launches')}
                      </th>
                      <th scope="col" className="num" translate="no">
                        p50
                      </th>
                      <th scope="col" className="num" translate="no">
                        p90
                      </th>
                      <th scope="col">{t('perf.metrickit.notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrickit.data.groups.map((g) => (
                      <tr key={g.release}>
                        <td className="mono" translate="no">
                          {g.release ? <ReleaseLink release={g.release} /> : '—'}
                        </td>
                        <td className="num">{compact(i18n, Math.round(g.count))}</td>
                        <td className="num">{ms(i18n, g.p50)}</td>
                        <td className="num">{ms(i18n, g.p90)}</td>
                        <td className="dim">
                          {[
                            g.multiple_versions ? t('perf.metrickit.multiple') : '',
                            g.excluded_points > 0
                              ? t('perf.metrickit.excluded', { count: g.excluded_points })
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' · ') || <span className="faint">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              <p className="pf-note">{rich('perf.metrickit.note')}</p>
            </Section>
          ) : null}
        </>
      )}
    </Page>
  )
}

// ---------------------------------------------------------------- hangs and ANRs

export function HangsPage() {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const { scope, app, appsPending, search } = usePerfScope()
  const { list, current, previous } = usePerfReleases(scope)
  const enabled = !!scope.app_id
  const hangs = useQuery({ ...hangsQuery(projectId, scope, true), enabled })
  const before = useQuery({
    ...hangsQuery(projectId, withRelease(scope, previous)),
    enabled: enabled && !!scope.release && !!previous,
  })
  const screens = useQuery({ ...screensQuery(projectId, scope, 'loads', 200), enabled })
  const android = app?.platform === 'android'
  const kind = android ? ('anr' as const) : ('hang' as const)
  const issues = useQuery({
    ...issuesQuery(projectId, {
      app_id: scope.app_id,
      release: scope.release,
      kind,
      days: scope.days,
      limit: 5,
      sort: 'users',
      daily: true,
    }),
    enabled,
  })

  const title = android ? t('perf.hangs.titleAnr') : t('perf.hangs.title')
  const page = t('perf.nav.hangs')
  if (appsPending || !app)
    return <Gate title={title} page={page} appsPending={appsPending} app={app} />

  const params = { orgId, projectId }
  const keep = { ...carried(search), app: scope.app_id }
  const h = hangs.data
  const all = hangCounts(h, android)
  const now = scope.release ? all : hangCounts(h, android, current)
  const was = scope.release ? hangCounts(before.data, android) : hangCounts(h, android, previous)
  const d = previous ? change(now.rate, was.rate) : null
  const loadsOf = new Map((screens.data ?? []).map((s) => [s.screen, s.loads]))
  const count = h
    ? android
      ? h.screens.reduce((n, s) => n + s.anrs, 0)
      : h.durations.count + h.durations.unfinished
    : 0
  const worstP90 = Math.max(-1, ...(h?.screens ?? []).map((s) => s.p90_ms ?? -1))
  const fmt1k = (v: number) => rate1k(i18n, v)
  const [p90v, p90u] = splitMs(i18n, h?.durations.p90_ms)

  const strip: StripItem[] = [
    {
      dt: android ? t('perf.card.anrRate') : t('perf.card.hangRate'),
      dd: rate1k(i18n, all.rate),
      // no unit beside a dash (no session in scope)
      ...(all.rate === null ? {} : { small: '/1k' }),
      d:
        all.rate === null ? (
          t('perf.card.noSessions')
        ) : previous ? (
          <Delta value={d} format={fmt1k} />
        ) : (
          t('perf.card.none')
        ),
    },
    ...(android
      ? []
      : [
          {
            dt: t('perf.hangs.duration'),
            dd: p90v,
            small: p90u,
            d: t('perf.card.p50', { value: ms(i18n, h?.durations.p50_ms) }),
          },
        ]),
    {
      dt: android ? t('perf.card.anr') : t('perf.card.hang'),
      dd: number(all.affected),
      d: t('perf.card.ofSessions', { total: all.sessions, sessions: compact(i18n, all.sessions) }),
    },
  ]

  return (
    <Page>
      <PerfHead
        title={title}
        page={page}
        meta={perfMeta(i18n, app, scope, [
          android ? t('perf.hangs.metaAnr') : t('perf.hangs.metaHang'),
          deltaMeta(t, scope, current, previous),
        ])}
        actions={
          <Link
            className={btnClass()}
            to="/o/$orgId/p/$projectId/issues"
            params={params}
            search={{ ...carried(search), app: scope.app_id, kind }}
          >
            {android ? t('perf.hangs.openAnrs') : t('perf.hangs.openHangs')}
          </Link>
        }
      />
      {hangs.isPending ? (
        <Loading />
      ) : hangs.isError ? (
        <ErrorState error={hangs.error} retry={() => hangs.refetch()} />
      ) : (
        <>
          <Strip items={strip} translate="no" />
          <div className="grid2">
            <div className="stack">
              <Section
                title={android ? t('perf.hangs.dailyAnr') : t('perf.hangs.dailyHang')}
                meta={t('perf.hangs.dailyMeta', { days: scope.days })}
              >
                <div className="pf-chart">
                  {h?.days.every((x) => x.sessions === 0) ? (
                    <EmptyNote title={t('perf.trend.empty')} />
                  ) : (
                    <LineChart
                      label={t('perf.hangs.dailyLabel', { days: scope.days })}
                      accent
                      area
                      timeAxis
                      xName={t('chart.day')}
                      marker={releaseMarker(
                        list,
                        current,
                        (h?.days ?? []).map((x) => x.day),
                      )}
                      points={(h?.days ?? []).map((x) => ({
                        label: dayLabel(i18n, x.day),
                        value: perThousand(
                          android ? x.sessions_with_anr : x.sessions_with_hang,
                          x.sessions,
                        ),
                      }))}
                      format={(v) => `${fmt1k(v)} /1k`}
                    />
                  )}
                </div>
              </Section>
              <Section title={t('perf.hangs.screens')} meta={t('perf.hangs.screensSub')}>
                {!h || h.screens.length === 0 ? (
                  <EmptyNote title={t('perf.hangs.noneOnScreens')} />
                ) : (
                  <>
                    <Table
                      label={t('table.of', { name: t('perf.hangs.screens') })}
                      rowKeys
                      className="pf-tbl"
                    >
                      <thead>
                        <tr>
                          <th scope="col">{t('vitals.col.screen')}</th>
                          <th scope="col" className="num">
                            {android ? t('perf.col.anrs') : t('perf.col.hangs')}
                          </th>
                          <th scope="col" className="num">
                            {t('perf.col.per1kLoads')}
                          </th>
                          {android ? null : (
                            <th scope="col" className="num">
                              {t('perf.col.p90Duration')}
                            </th>
                          )}
                          <th scope="col" className="num">
                            {t('releases.sessions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {h.screens.map((s) => {
                          const n = android ? s.anrs : s.hangs
                          const loads = loadsOf.get(s.screen)
                          const cells = (
                            <>
                              <td className="mono anywhere" translate="no">
                                {s.screen ? (
                                  <Link
                                    to="/o/$orgId/p/$projectId/performance/screens/detail"
                                    params={params}
                                    search={{ ...keep, screen: s.screen }}
                                  >
                                    {s.screen}
                                  </Link>
                                ) : (
                                  <span className="dim">{t('perf.hangs.noScreen')}</span>
                                )}
                              </td>
                              <td className="num">{number(n)}</td>
                              <td className="num">
                                {s.screen && loads ? (
                                  <span
                                    className={
                                      s.p90_ms !== null && s.p90_ms === worstP90
                                        ? 'c-warn'
                                        : undefined
                                    }
                                  >
                                    {rate1k(i18n, (n / loads) * 1000)}
                                  </span>
                                ) : (
                                  <span className="faint">—</span>
                                )}
                              </td>
                              {android ? null : (
                                <td className="num">
                                  {s.p90_ms !== null && s.p90_ms === worstP90 ? (
                                    <Pill tone="warn">{ms(i18n, s.p90_ms)}</Pill>
                                  ) : (
                                    ms(i18n, s.p90_ms)
                                  )}
                                </td>
                              )}
                              <td className="num">{number(s.sessions)}</td>
                            </>
                          )
                          return s.screen ? (
                            <HovRow
                              key={`${s.app_id} ${s.screen}`}
                              onOpen={() =>
                                navigate({
                                  to: '/o/$orgId/p/$projectId/performance/screens/detail',
                                  params,
                                  search: { ...keep, screen: s.screen },
                                })
                              }
                            >
                              {cells}
                            </HovRow>
                          ) : (
                            <tr key={`${s.app_id} -`}>{cells}</tr>
                          )
                        })}
                      </tbody>
                    </Table>
                    <TableFoot>
                      <span>
                        {t(android ? 'perf.hangs.footAnr' : 'perf.hangs.foot', {
                          count,
                          sessions: all.affected,
                        })}
                      </span>
                    </TableFoot>
                  </>
                )}
              </Section>
              <Section
                title={android ? t('perf.hangs.topAnr') : t('perf.hangs.topHang')}
                meta={
                  issues.data
                    ? t('perf.hangs.topMeta', { count: issues.data.issues.length })
                    : undefined
                }
                right={
                  <Link
                    className={btnClass('ghost', 'sm')}
                    to="/o/$orgId/p/$projectId/issues"
                    params={params}
                    search={{ ...carried(search), app: scope.app_id, kind }}
                  >
                    {t('perf.hangs.allIssues')}
                  </Link>
                }
              >
                {issues.isPending ? (
                  <Loading />
                ) : issues.isError ? (
                  <ErrorState error={issues.error} retry={() => issues.refetch()} />
                ) : issues.data.issues.length === 0 ? (
                  <EmptyNote title={t('perf.hangs.noIssues')} />
                ) : (
                  <IssueTable
                    label={t('table.of', {
                      name: android ? t('perf.hangs.topAnr') : t('perf.hangs.topHang'),
                    })}
                    issues={issues.data.issues}
                  />
                )}
              </Section>
            </div>
            <div className="stack">
              {/* ANRs have no duration: Android hides the section. On iOS it stays, and says why
                  when no duration was measured (the APS-385 rule; Mosaic, APS-392). */}
              {!android && h ? (
                <Section
                  title={t('perf.hangs.durations')}
                  meta={
                    h.durations.count > 0
                      ? t('perf.hangs.durationsMeta', {
                          count: h.durations.count,
                          width: h.durations.fine_histogram?.bucket_ms ?? 250,
                        })
                      : undefined
                  }
                >
                  {h.durations.count > 0 ? (
                    <Histogram
                      data={h.durations.histogram}
                      fine={h.durations.fine_histogram}
                      p50={h.durations.p50_ms}
                      p90={h.durations.p90_ms}
                      height={170}
                      ticks={[250, 1000, 2000]}
                      label={t('perf.hangs.durationsLabel', { count: h.durations.count })}
                      countName={(count) => t(android ? 'chart.anrs' : 'chart.hangs', { count })}
                    />
                  ) : null}
                  {h.durations.unfinished > 0 ? (
                    <p className="pf-cap-note">
                      {t('perf.hangs.unfinished', { count: h.durations.unfinished })}
                    </p>
                  ) : null}
                  {h.durations.count + h.durations.unfinished === 0 ? (
                    <p className="se-p dim">{t('perf.hangs.noDurations')}</p>
                  ) : null}
                </Section>
              ) : null}
              <Section
                title={t('perf.hangs.releases')}
                meta={android ? t('perf.hangs.releasesMetaAnr') : t('perf.hangs.releasesMeta')}
              >
                {!h || h.releases.length === 0 ? (
                  <EmptyNote title={t('perf.card.none')} />
                ) : (
                  <Table
                    label={t('table.of', { name: t('perf.hangs.releases') })}
                    className="pf-tbl"
                  >
                    <thead>
                      <tr>
                        <th scope="col">{t('releases.col.release')}</th>
                        <th scope="col" className="num">
                          {t('releases.sessions')}
                        </th>
                        <th scope="col" className="num">
                          {android ? t('perf.card.anrRate') : t('perf.card.hangRate')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {h.releases.map((r) => {
                        const v = perThousand(
                          android ? r.sessions_with_anr : r.sessions_with_hang,
                          r.sessions,
                        )
                        return (
                          <tr key={`${r.app_id} ${r.release}`}>
                            <td className="mono" translate="no">
                              <ReleaseLink release={r.release} />
                            </td>
                            <td className="num">{compact(i18n, r.sessions)}</td>
                            <td className="num">
                              <span
                                className={r.release === current ? 'c-warn' : undefined}
                                translate="no"
                              >
                                {`${rate1k(i18n, v)} /1k`}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Section>
            </div>
          </div>
        </>
      )}
    </Page>
  )
}
